import {
  IconArrowsSort,
  IconLock,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react"
import { flexRender } from "@tanstack/react-table"
import { match } from "ts-pattern"

import { cn } from "@/lib/utils"

import { ExplainerTooltip } from "../../ExplainerTooltip"
import { Tooltip } from "../../Tooltip"
import { SELECTABLE_COLUMN_ID } from "../DataTable"
import { LE_TableInstance } from "../types"
import { ColumnDropdown } from "./ColumnDropdown"

export type TablePosition = "left" | "center" | "right"

export function TableHead<TData extends Record<string, any> = object>({
  table,
  classNameHeader,
  showColumnKebabMenu,
  position,
}: {
  table: LE_TableInstance<TData>
  classNameHeader?: string
  showColumnKebabMenu: boolean
  position?: TablePosition
}) {
  return (
    <thead
      className={cn("z-20 p-3", "border border-solid border-border", classNameHeader)}
    >
      {(position === "left"
        ? table.getLeftHeaderGroups()
        : position === "center"
          ? table.getCenterHeaderGroups()
          : position === "right"
            ? table.getRightHeaderGroups()
            : table.getHeaderGroups()
      ).map((headerGroup, index, headerGroups) => {
        const isLastHeaderRow = index === headerGroups.length - 1

        return (
          <tr
            key={headerGroup.id}
            className="sticky z-20 items-center"
            style={{
              top: `${index * 2.5}rem`,
              height: "2.25rem",
            }}
          >
            {headerGroup.headers.map((header) => {
              const col = header.column
              const HeaderIcon = col.columnDef.meta?.icon
              const hasChildren = col.getFlatColumns().length > 1
              const colParent = col.parent
              const lastParentChild = colParent?.getLeafColumns().at(-1)?.id === col.id

              const isGroupPinned = header.subHeaders.some((subHeader) =>
                subHeader.column.getIsPinned(),
              )
              const isPinned = hasChildren ? isGroupPinned : col.getIsPinned()

              return (
                <th
                  className={cn(
                    "sticky top-0 z-20 bg-neutral px-4 py-2 text-start align-top text-sm whitespace-nowrap text-muted-foreground",
                    "border-solid border-border font-normal last-of-type:border-r-0",
                    "h-10",

                    isLastHeaderRow ? "border-b-2" : "",
                    hasChildren && "border-r p-1 font-medium",
                    lastParentChild && "border-r",
                    isPinned && "bg-neutral",
                    classNameHeader,
                  )}
                  key={header.id}
                  style={{
                    width: col.columnDef.size ? header.getSize() : undefined,
                    ...(col.columnDef.meta?.fixedSize || isPinned
                      ? {
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          maxWidth: header.getSize(),
                        }
                      : {}),
                  }}
                  colSpan={header.colSpan}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className={cn(
                        "flex h-full cursor-default items-center justify-between gap-3 overflow-hidden rounded",
                        hasChildren && "bg-muted-foreground/15 px-3 py-1.5",
                        hasChildren && isPinned && "bg-muted-foreground/25",
                      )}
                    >
                      <span className="flex min-w-0 shrink items-center gap-3">
                        {HeaderIcon ? <HeaderIcon size={16} /> : null}
                        <span
                          className={cn(
                            "flex w-fit min-w-0 shrink items-center gap-1",
                            {
                              "sr-only": col.columnDef.meta?.hideHeader,
                              "cursor-pointer": col.getCanSort(),
                            },
                          )}
                          onClick={col.getToggleSortingHandler()}
                        >
                          <span
                            className="truncate"
                            title={flexRender(
                              col.columnDef.header,
                              header.getContext(),
                            )?.toString()}
                          >
                            {flexRender(col.columnDef.header, header.getContext())}
                          </span>
                          {col.getCanSort() ? (
                            <Tooltip content="Toggle sorting" disableHoverablePopup>
                              <button aria-label="toggle sorting" type="button">
                                {match(col.getIsSorted())
                                  .with(false, () => <IconArrowsSort size={16} />)
                                  .with("asc", () => (
                                    <IconSortAscending
                                      size={16}
                                      className="text-foreground"
                                    />
                                  ))
                                  .with("desc", () => (
                                    <IconSortDescending
                                      size={16}
                                      className="text-foreground"
                                    />
                                  ))
                                  .exhaustive()}
                              </button>
                            </Tooltip>
                          ) : null}
                        </span>
                      </span>
                      {col.id !== SELECTABLE_COLUMN_ID &&
                      (showColumnKebabMenu ||
                        col.columnDef.meta?.tooltip ||
                        col.columnDef.meta?.adminOnly) ? (
                        <span className="flex items-center gap-2">
                          {col.columnDef.meta?.tooltip ? (
                            <ExplainerTooltip
                              content={col.columnDef.meta.tooltip}
                              tooltipClassName="max-w-sm"
                            />
                          ) : null}
                          {col.columnDef.meta?.adminOnly && (
                            <Tooltip
                              content="This column is admin-only. Non-admin users will be unable to see it."
                              className="max-w-sm"
                              disableHoverablePopup
                            >
                              <IconLock />
                            </Tooltip>
                          )}
                          {showColumnKebabMenu ? (
                            <ColumnDropdown col={col} table={table} />
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  )}
                </th>
              )
            })}
          </tr>
        )
      })}
    </thead>
  )
}
